import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { sanitizeInput } from '../../helpers/sanitizeInput.helper';
import {
  WORLD_CUP_GROUP_DESCRIPTION_MAX_LENGTH,
  WORLD_CUP_PAYOUT_MODE,
} from '../worldcup.constants';
import type { WorldCupPayoutMode } from '../worldcup.constants';

export interface CreateWorldCupSquadRequestBody {
  name: string;
  description?: string;
  crestColor?: string;
  crestEmblem?: string;
  entryFeeCoins: number;
  payoutMode: WorldCupPayoutMode;
  payoutFirstPct?: number;
  payoutSecondPct?: number;
  payoutThirdPct?: number;
}

export interface CreateWorldCupSquadResult {
  id: string;
  inviteCode: string;
  name: string;
}

interface CreateSquadApiPayload {
  success: boolean;
  error?: string;
  data?: { id: string; inviteCode: string; name: string };
}

type ApiInvoker = <T>(
  endpoint: string,
  options?: AxiosRequestConfig,
) => Promise<AxiosResponse<T>>;

export async function createWorldCupCompetitionGroup(
  apiClient: ApiInvoker,
  competitionId: string,
  body: CreateWorldCupSquadRequestBody,
): Promise<CreateWorldCupSquadResult> {
  const sanitizedName = sanitizeInput(body.name.trim(), true);
  const requestPayload: CreateWorldCupSquadRequestBody = {
    name: sanitizedName,
    ...(body.crestColor ? { crestColor: body.crestColor } : {}),
    ...(body.crestEmblem ? { crestEmblem: body.crestEmblem } : {}),
    entryFeeCoins: body.entryFeeCoins,
    payoutMode: body.payoutMode,
    ...(body.payoutMode === WORLD_CUP_PAYOUT_MODE.TOP_THREE
      ? {
          payoutFirstPct: body.payoutFirstPct,
          payoutSecondPct: body.payoutSecondPct,
          payoutThirdPct: body.payoutThirdPct,
        }
      : {}),
  };
  if (typeof body.description === 'string' && body.description.trim().length > 0) {
    requestPayload.description = sanitizeInput(body.description.trim(), true).slice(
      0,
      WORLD_CUP_GROUP_DESCRIPTION_MAX_LENGTH,
    );
  }

  const res = await apiClient<CreateSquadApiPayload>(`/competitions/${encodeURIComponent(competitionId)}/groups`, {
    method: 'POST',
    data: requestPayload,
  });
  const resBody = res.data;
  if (!resBody.success || !resBody.data?.id) {
    throw new Error(resBody.error || 'Failed to create squad');
  }
  return {
    id: resBody.data.id,
    inviteCode: resBody.data.inviteCode,
    name: resBody.data.name,
  };
}

interface JoinSquadApiPayload {
  success: boolean;
  error?: string;
  data?: { groupId: string };
}

export async function joinWorldCupCompetitionGroup(
  apiClient: ApiInvoker,
  competitionId: string,
  inviteCode: string,
): Promise<{ groupId: string }> {
  const sanitizedInviteCode = sanitizeInput(inviteCode.trim(), true).trim().toUpperCase();
  const res = await apiClient<JoinSquadApiPayload>(`/competitions/${encodeURIComponent(competitionId)}/groups/join`, {
    method: 'POST',
    data: { inviteCode: sanitizedInviteCode },
  });
  const payload = res.data;
  if (!payload.success || !payload.data?.groupId) {
    throw new Error(payload.error || 'Failed to join squad');
  }
  return { groupId: payload.data.groupId };
}

export interface RemoveWorldCupGroupMemberResult {
  groupDeleted: boolean;
  newAdminMembershipId: string | null;
}

interface RemoveMemberApiPayload {
  success: boolean;
  error?: string;
  message?: string;
  data?: {
    groupDeleted: boolean;
    newAdminMembershipId: string | null;
  };
}

/**
 * Kick a member from a squad (admin only) or leave the squad (self).
 * Backend handles admin transfer and squad deletion when the last human leaves.
 */
export async function removeWorldCupCompetitionGroupMember(
  apiClient: ApiInvoker,
  competitionId: string,
  groupId: string,
  membershipId: string,
): Promise<RemoveWorldCupGroupMemberResult> {
  const res = await apiClient<RemoveMemberApiPayload>(
    `/competitions/${encodeURIComponent(competitionId)}/groups/${groupId}/members/${membershipId}`,
    { method: 'DELETE' },
  );
  const payload = res.data;
  if (!payload.success) {
    throw new Error(payload.error || 'Failed to remove member');
  }
  return {
    groupDeleted: payload.data?.groupDeleted ?? false,
    newAdminMembershipId: payload.data?.newAdminMembershipId ?? null,
  };
}

/** Leave the caller's squad. Wraps `removeWorldCupCompetitionGroupMember` with the caller's own membershipId. */
export async function leaveWorldCupCompetitionGroup(
  apiClient: ApiInvoker,
  competitionId: string,
  groupId: string,
  currentUserMembershipId: string,
): Promise<RemoveWorldCupGroupMemberResult> {
  return removeWorldCupCompetitionGroupMember(apiClient, competitionId, groupId, currentUserMembershipId);
}

interface PatchSquadApiPayload {
  success: boolean;
  error?: string;
  data?: { id: string; name: string };
}

export async function patchWorldCupCompetitionGroup(
  apiClient: ApiInvoker,
  competitionId: string,
  groupId: string,
  body: { name: string },
): Promise<{ id: string; name: string }> {
  const sanitizedName = sanitizeInput(body.name.trim(), true);
  const res = await apiClient<PatchSquadApiPayload>(
    `/competitions/${encodeURIComponent(competitionId)}/groups/${groupId}`,
    {
      method: 'PATCH',
      data: { name: sanitizedName },
    },
  );
  const resBody = res.data;
  if (!resBody.success || !resBody.data?.id) {
    throw new Error(resBody.error || 'Failed to update squad');
  }
  return { id: resBody.data.id, name: resBody.data.name };
}
